// The analytic model behind the District Insights dashboard: the numbers every
// page reads, derived once here from the windowed-stats payloads.
//
// The chain runs one way. `buildSeries` normalizes and rebases one segment's
// windowed response; `toSegmentRows` does that for a segmented response;
// `buildPoints` adds the per-segment reach/depth/trend math; and then either
// `buildQuad` (peers vs. the district centroid, with an outlier distance) or
// `buildCohortRows` (each row vs. a matched peer) layers the comparison on top.
// Alongside them sit the statistics they need and the data-sufficiency bars that
// decide when a figure is too thin to judge -- MIN_OUTLIER_N, MIN_COHORT_ENGAGED,
// SCHOOL_MIN_LEARNERS -- kept together because they answer one question at three
// scales: how much data before we are willing to say something.
//
// No React, no imports: pure functions over payload shapes.

const MASTERED_KEYS = ["mastered_outright", "eventually_mastered"];
const SUBJECTS = ["MATH", "READING"];

const median = (nums) => {
  if (!nums.length) return 0;
  const s = [...nums].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Robust scale (≈ std dev, but resistant to the very outliers we're hunting):
// 1.4826 * median-absolute-deviation. When >half the values tie the MAD is 0, so
// fall back to the plain std dev, then to 1 (a district with no spread has no
// outliers). Used to standardize the reach and depth axes -- they're on wildly
// different ranges (0–100 vs ~0–30), so a raw distance would be all reach.
const robustScale = (vals, center) => {
  if (!vals.length) return 1;
  const mad = median(vals.map((v) => Math.abs(v - center)));
  const s = 1.4826 * mad;
  if (s > 0) return s;
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
  const sd = Math.sqrt(
    vals.reduce((a, b) => a + (b - mean) ** 2, 0) / vals.length
  );
  return sd > 0 ? sd : 1;
};
// A school is an outlier once it sits this many robust std devs from the district
// centroid (Euclidean, in standardized reach/depth space). 1.5σ ≈ "notably
// unusual" without flagging half the district the way a median split does.
const OUTLIER_SIGMA = 1.5;
// Below this many schools there's no meaningful "district normal" to deviate
// from -- robust stats degenerate (with 2 schools both land at ~1.4σ), and the
// plot's comparative value is small anyway. Under it we drop outlier mode and
// just color every dot solidly by its own standing. Tunable judgment call.
const MIN_OUTLIER_N = 5;

// A school's slice of a grade cohort is a fraction of it, so per-school rows and
// matrix cells need a much lower coverage bar than the district-level unit list:
// a unit with 40 learners district-wide is ~5 per school across 8 schools. Rows
// below it are marked and de-weighted, never hidden -- a school vanishing from a
// comparison reads as "no gap here", the opposite of what it means. Sits with the
// other sufficiency bars rather than with the curriculum table that reads it,
// because the units x schools matrix has to bin on the same number.
const SCHOOL_MIN_LEARNERS = 5;

// sum a per-boundary {SUBJECT: {status: n}} snapshot over subjects + given keys
const subjSum = (pt, keys) =>
  SUBJECTS.reduce(
    (acc, subj) =>
      acc + keys.reduce((a, k) => a + ((pt[subj] && pt[subj][k]) || 0), 0),
    0
  );

// windowed response -> normalized per-boundary points.
//
// The backend series is CUMULATIVE OVER ALL TIME (the only time filter is
// event_at <= boundary; there is no >= start). Everything shown on this page must
// reflect only the selected school year, so we rebase here in one place:
//   - mastery counts -> subtract the window-start baseline (series[0].mastered),
//     which is exactly the pre-window total. masteredInWindow is the standards
//     mastered *since the window start*.
//   - engaged learners -> use learner_counts_in_window (distinct learners with an
//     event inside the window). This can't be derived by differencing the
//     all-time learner_counts (a returning learner would cancel out), so the
//     backend emits it directly. Older backends omit it; fall back to the
//     all-time count so the page still renders (reach will read high).
// perLearnerInWindow divides the in-window mastery gain by the in-window engaged
// count -- both scoped to the year, so the ratio is school-year-only too.
const buildSeries = (win) => {
  if (!win || !win.subsets || !win.subsets.all) return null;
  const all = win.subsets.all;
  const ts = win.timestamps || [];
  const lc = win.learner_counts || [];
  const lcWin = win.learner_counts_in_window || null;
  // cumulative mastered for one subject at a boundary snapshot
  const subjMastered = (pt, subj) =>
    MASTERED_KEYS.reduce((a, k) => a + ((pt[subj] && pt[subj][k]) || 0), 0);
  const pts = all.map((pt, i) => {
    const mastered = subjSum(pt, MASTERED_KEYS);
    const total = subjSum(pt, [...MASTERED_KEYS, "not_mastered"]);
    const learners = lc[i] || 0;
    const bySubject = {};
    SUBJECTS.forEach((s) => {
      bySubject[s] = subjMastered(pt, s);
    });
    return {
      t: ts[i],
      mastered,
      total,
      learners,
      learnersInWindow: lcWin ? lcWin[i] || 0 : learners,
      bySubject,
    };
  });
  // trim leading boundaries with no engaged learners so lines/trails start
  // where the school actually has data (cumulative, so this only drops zeros)
  const first = pts.findIndex((p) => p.learners > 0);
  const trimmed = first > 0 ? pts.slice(first) : pts;
  const base = trimmed.length ? trimmed[0].mastered : 0;
  // rebase each subject to its own window-start total so the per-subject
  // in-window counts still sum to masteredInWindow (base = sum of subjBase)
  const subjBase = {};
  SUBJECTS.forEach((s) => {
    subjBase[s] = trimmed.length ? trimmed[0].bySubject[s] : 0;
  });
  return trimmed.map((p) => {
    const bySubjectInWindow = {};
    SUBJECTS.forEach((s) => {
      bySubjectInWindow[s] = p.bySubject[s] - subjBase[s];
    });
    return {
      ...p,
      masteredInWindow: p.mastered - base,
      perLearnerInWindow:
        p.learnersInWindow > 0 ? (p.mastered - base) / p.learnersInWindow : 0,
      bySubjectInWindow,
    };
  });
};

// The windowed response's `recent` block, normalized -- what moved in the
// trailing four weeks, which is a different question from what the last bucket
// of the series holds and must not be derived from it.
//
// The series' last two boundaries are the current calendar month clipped to
// today, so differencing them (which is what the Overview's "This month" card
// used to do) reports one day's mastery on the 1st and a full month's on the
// 31st. The backend folds this span in the same pass over the same rows, so the
// number is consistent with the series by construction rather than by hoping two
// derivations agree.
//
// Null for a payload without the block, so callers keep a "not available" path
// rather than rendering a confident zero.
const buildRecent = (win) => {
  const r = win && win.recent;
  if (!r) return null;
  return {
    days: r.days,
    mastered: r.mastered_gained || 0,
    learners: r.learners_engaged || 0,
  };
};

// classify by depth (value axis) first, then reach
const classify = (reach, depth, medReach, medDepth) => {
  if (depth < medDepth) return "critical";
  return reach >= medReach ? "good" : "warning";
};

// Re-trim a full-length per-boundary array so it indexes with a built series.
//
// The server emits one entry per boundary, but buildSeries drops the LEADING
// boundaries where nothing had engaged yet (so a school's line starts where its
// data does). Anything indexed against the series has to lose the same head or
// every bucket reads one too early — which for a cycle band means tinting the
// wrong month, and for concentration means measuring a push that isn't there.
// Because only the head is trimmed, aligning from the tail is exact, and it
// can't drift if the trim rule is ever changed.
//
// Returns undefined for a missing input (the field is opt-in) and leaves a
// too-short array alone rather than fabricating alignment.
const alignToSeries = (values, series) => {
  if (!values || !series) return undefined;
  if (values.length < series.length) return undefined;
  return values.slice(values.length - series.length);
};

// A segmented-endpoint response -> the normalized row shape every model below
// consumes ({id, name, series, roster, current}). Segments whose series can't be
// built (no subsets) drop out; the roster filter is left to the caller because
// the engagement view wants empty tiers to keep their legend slot.
const toSegmentRows = (segments) =>
  (segments || [])
    .map((s) => {
      const series = buildSeries(s);
      if (!series || !series.length) return null;
      return {
        id: s.id,
        name: s.name,
        // The string this row ORDERS on when it differs from the one it prints.
        // Only the teacher dimension sends it (people are listed given-name-first
        // and ordered surname-first) and only the server can build it, since it
        // is the only side that knows where the given name ends -- see
        // LearnerSegments.teacher_sort_names and format.js's nameSortKey.
        sortName: s.sort_name,
        series,
        roster: s.roster_size != null ? s.roster_size : 0,
        current: series[series.length - 1],
        // Present only when the request asked for them (`include=volume,dosage`);
        // undefined otherwise, which is what lets the status cascade fall through
        // to the median split rather than inventing a label from missing data.
        //
        // `volume` is index-aligned with `series`: entry i counts the lessons in
        // the span ENDING at series[i], the same span momentum[i] describes. The
        // server pads index 0 to zero for exactly that reason — see
        // vanroboapp/segments/volume.py.
        volume: alignToSeries(s.volume_by_bucket, series),
        dosage: s.dosage,
        atTarget: s.at_target,
        atTargetShare: s.at_target_share,
      };
    })
    .filter(Boolean);

// Per-segment reach/depth/trend math — everything that depends only on the
// segment itself, with no reference to its peers. buildQuad layers the district
// centroid + outlier distance on top; the grade drill-down (buildCohortRows)
// layers a grade-matched peer comparison on top instead.
const buildPoints = (segments) =>
  segments
    .filter((s) => s.roster > 0)
    .map((s) => {
      const reach = clamp((100 * s.current.learnersInWindow) / s.roster, 0, 100);
      const depth = s.current.perLearnerInWindow;
      // Comet trail: the last six buckets as (reach, depth) pairs.
      //
      // Leading buckets where NOTHING had happened yet are dropped, because those
      // sit exactly on the origin and the segment out of them is an artifact of
      // the window rather than a movement. Early in a school year that artifact is
      // the whole picture — viewing Dillon one month into 2026–27, every school's
      // trail would be a straight line from (0, 0) to its dot, which says only
      // "the year started", identically for everyone, while drawing the eye along
      // a path nobody travelled.
      //
      // The test is reach OR depth, not both: a bucket with learners engaged and
      // no mastery yet is a real position on the plot (bottom edge, not the
      // corner) and the trail should start there. The series is cumulative in the
      // window, so zeros only ever occur as a leading run — nothing to trim at the
      // other end. Fewer than two surviving points means no trail, which is the
      // honest answer for a segment with one bucket of history.
      const rawTail = s.series.slice(-6).map((p) => ({
        reach: clamp((100 * p.learnersInWindow) / s.roster, 0, 100),
        depth: p.perLearnerInWindow,
      }));
      const firstLive = rawTail.findIndex((p) => p.reach > 0 || p.depth > 0);
      const tail = firstLive < 0 ? [] : rawTail.slice(firstLive);
      // per-period momentum: new standards mastered per engaged learner in each
      // bucket (non-negative; this is what the Trend sparkline shows)
      const momentum = s.series.map((p, i) => {
        const gain =
          i > 0 ? p.masteredInWindow - s.series[i - 1].masteredInWindow : 0;
        return p.learnersInWindow > 0 ? gain / p.learnersInWindow : 0;
      });
      const reachSeries = s.series.map((p) =>
        clamp((100 * p.learnersInWindow) / s.roster, 0, 100)
      );
      const n = reachSeries.length;
      const reachDelta = n > 1 ? reachSeries[n - 1] - reachSeries[n - 2] : null;
      const depthDelta = momentum.length ? momentum[momentum.length - 1] : null;
      return { ...s, reach, depth, tail, momentum, reachDelta, depthDelta };
    });

// Quadrant model (reach × depth) over normalized segment rows ({id, name,
// series, roster, current}). Returns one point per segment with reach/depth,
// comet tail, momentum sparkline values, last-period deltas, quadrant status,
// and robust-σ distance from the district centroid (the outlier signal). This
// is the one model behind the Adoption page's Outliers lens, and the Overview
// digest reuses it for the outlier count and its QuadSpark.
const buildQuad = (segments) => {
  const pts = buildPoints(segments);
  // Center = district median; standardize each axis by its robust scale so
  // distance isn't dominated by the 0–100 reach range.
  const medReach = median(pts.map((p) => p.reach));
  const medDepth = median(pts.map((p) => p.depth));
  const scaleReach = robustScale(pts.map((p) => p.reach), medReach);
  const scaleDepth = robustScale(pts.map((p) => p.depth), medDepth);
  return pts.map((p) => {
    const zReach = (p.reach - medReach) / scaleReach;
    const zDepth = (p.depth - medDepth) / scaleDepth;
    const dist = Math.hypot(zReach, zDepth);
    return {
      ...p,
      status: classify(p.reach, p.depth, medReach, medDepth),
      medReach,
      medDepth,
      zReach,
      zDepth,
      dist,
      isOutlier: dist >= OUTLIER_SIGMA,
    };
  });
};

// Cohort drill-down model: a segment broken into the sub-segments inside it,
// each binned against a matched peer rather than against a global median. Used
// at both drill levels of the school worklist:
//   school -> grade cohorts, each vs the SAME grade district-wide  (refById)
//   grade cohort -> teachers, each vs the cohort they sit in       (refConst)
//
// Why grade-matched and not the school-level crosshair: reach and depth both
// vary systematically with grade level. A kindergarten cohort masters far fewer
// standards per engaged learner than a 5th-grade one, and typically starts later
// in the year. Judged against a median computed over whole-school aggregates,
// every K cohort in the district would read "needs support" and every 4–5 cohort
// "thriving" — the status column would be reporting grade, not school
// performance. Comparing K to K and 5 to 5 removes that confound, so what's left
// is the thing the worklist is actually about: is this cohort behind its peers?
//
// The bins keep `classify`'s three-way shape with the district's same-grade
// cohort standing in for the district centroid, but add a relative deadband (see
// COHORT_PEER_TOLERANCE). No σ / outlier ranking here: there's exactly one
// reference point per grade, not a distribution (getting one would mean a
// schools × grades fan-out on every expand), so the honest read is the gap
// against that reference, which each row carries as refReach/refDepth.
//
// `peerState` says whether a comparison was possible at all, and `status` is
// null unless it's "ok":
//   ok    — compared against a real, active peer cohort
//   none  — the grade doesn't exist district-wide (shouldn't happen: school ⊆
//           district — but don't invent a status if it does)
//   sole  — the reference cohort is essentially this row (the district's whole
//           grade sits at this school; one teacher holds the cohort), so the
//           comparison would be the row against itself — always thriving
//   idle  — a peer cohort exists but nobody in it engaged this year, so both
//           sides are zero and every bin is vacuous. Without this guard a cohort
//           that did nothing all year reads "Thriving" next to an equally dead
//           district — the exact opposite of what a worklist should say.
const COHORT_PEER_STATES = { ok: "ok", none: "none", sole: "sole", idle: "idle" };
// Relative deadband on the peer comparison. The parent worklist splits on a
// median taken over many schools; a cohort has ONE reference point and a roster
// often in the single digits, so a strict split turns rounding into red — 1.7
// std/learner against a district 1.8 is not a finding. Within ±10% of the peer
// counts as "at peer" on that axis. Judgment call, deliberately tunable.
const COHORT_PEER_TOLERANCE = 0.1;
// Below this many *engaged* learners the depth number is one or two transcripts;
// the row still gets a status (suppressing it hides real signal) but is marked
// thin so nobody reads it as a trend.
const MIN_COHORT_ENGAGED = 5;
// A row holding this much of its reference is being compared with itself: the
// remainder is too small to move the reference, so the comparison can only come
// out at-or-above peer no matter how the row is doing. Exact equality isn't
// enough of a guard — a teacher with 500 of a cohort's 502 learners reads
// "Thriving" against a reference it IS. Treated as `sole`.
const COHORT_SOLE_SHARE = 0.9;

const classifyVsPeer = (reach, depth, refReach, refDepth) => {
  const tol = 1 - COHORT_PEER_TOLERANCE;
  if (depth < refDepth * tol) return "critical";
  return reach >= refReach * tol ? "good" : "warning";
};

// Which reference point each row is judged against. Two shapes are in use:
//   refById   — match the peer by segment id (a grade cohort against the same
//               grade district-wide). Segments in, so it does its own
//               buildPoints pass.
//   refConst  — every row against one already-built point (each teacher
//               against the grade cohort they hold a slice of). Part-vs-whole is
//               the intended read: which teacher is holding the cohort back.
const refById = (referenceSegments) => {
  const ref = new Map(
    buildPoints(referenceSegments || []).map((p) => [p.id, p])
  );
  return (p) => ref.get(p.id) || null;
};
const refConst = (point) => () => point || null;

const buildCohortRows = (cohortSegments, resolveRef) => {
  return buildPoints(cohortSegments || []).map((p) => {
    const r = resolveRef ? resolveRef(p) : null;
    let peerState = COHORT_PEER_STATES.ok;
    if (!r) peerState = COHORT_PEER_STATES.none;
    else if (p.roster >= r.roster * COHORT_SOLE_SHARE)
      peerState = COHORT_PEER_STATES.sole;
    else if (r.reach <= 0) peerState = COHORT_PEER_STATES.idle;
    const peer = peerState === COHORT_PEER_STATES.ok ? r : null;
    const engaged = p.current ? p.current.learnersInWindow : 0;
    return {
      ...p,
      engaged,
      thin: engaged < MIN_COHORT_ENGAGED,
      refReach: peer ? peer.reach : null,
      refDepth: peer ? peer.depth : null,
      refRoster: peer ? peer.roster : null,
      // the reference's size whatever the state, so a suppressed row can still
      // say *how* dominant it is ("500 of 502") instead of asserting it is all
      peerRoster: r ? r.roster : null,
      peerState,
      status: peer
        ? classifyVsPeer(p.reach, p.depth, peer.reach, peer.depth)
        : null,
    };
  });
};

// Which buckets of a series fall inside each detected focus cycle, as inclusive
// [lo, hi] index pairs. Returns null when there is nothing to mark.
//
// Computed per series rather than once for the view, because buildSeries drops the
// LEADING boundaries where a segment had no engaged learners — so bucket 3 is a
// different month for a school that started in August than for one that started in
// October. A single shared band would tint the wrong stretch on every late
// starter, which is precisely the population the annotation is most useful for.
//
// A bucket counts as in-cycle if its span overlaps the cycle at all. Buckets are
// half-open spans ending at their own timestamp — bucket i covers
// (series[i-1].t, series[i].t] — matching how momentum and lesson volume are
// indexed. Index 0 is the window-start instant and so is never inside anything.
const cycleBandsForSeries = (series, cycles) => {
  if (!series || series.length < 2 || !cycles || !cycles.length) return null;
  const ms = (v) => new Date(v).getTime();
  const out = [];
  cycles.forEach((c) => {
    const cs = ms(c.start);
    const ce = ms(c.end);
    if (Number.isNaN(cs) || Number.isNaN(ce)) return;
    let lo = -1;
    let hi = -1;
    for (let i = 1; i < series.length; i++) {
      const bEnd = ms(series[i].t);
      const bStart = ms(series[i - 1].t);
      if (bEnd > cs && bStart < ce) {
        if (lo < 0) lo = i;
        hi = i;
      }
    }
    if (lo >= 0) out.push([lo, hi]);
  });
  return out.length ? out : null;
};

// The same question for a payload that carries explicit bucket spans (the
// engagement mix strip's `comp.buckets`), where no trimming happens and each
// bucket states its own start and end.
const cycleBandsForBuckets = (buckets, cycles) => {
  if (!buckets || !buckets.length || !cycles || !cycles.length) return null;
  const ms = (v) => new Date(v).getTime();
  const out = [];
  cycles.forEach((c) => {
    const cs = ms(c.start);
    const ce = ms(c.end);
    if (Number.isNaN(cs) || Number.isNaN(ce)) return;
    let lo = -1;
    let hi = -1;
    buckets.forEach((b, i) => {
      if (ms(b.end) > cs && ms(b.start) < ce) {
        if (lo < 0) lo = i;
        hi = i;
      }
    });
    if (lo >= 0) out.push([lo, hi]);
  });
  return out.length ? out : null;
};


export {
  MASTERED_KEYS,
  median,
  clamp,
  robustScale,
  OUTLIER_SIGMA,
  MIN_OUTLIER_N,
  SCHOOL_MIN_LEARNERS,
  subjSum,
  buildSeries,
  buildRecent,
  alignToSeries,
  toSegmentRows,
  buildPoints,
  buildQuad,
  COHORT_PEER_STATES,
  COHORT_PEER_TOLERANCE,
  refById,
  refConst,
  buildCohortRows,
  cycleBandsForSeries,
  cycleBandsForBuckets,
};
